import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  User,
  Building2,
  Clock,
  Pencil,
  Check,
  X,
  Users,
  Save,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Copy,
  MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';
import TenantMobileLayout from './TenantMobileLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const TenantMobileJobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { api } = useAuth();
  const { canUseChat, openJobChat } = useChat();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Reference data
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Allocation sheet
  const [showAllocationSheet, setShowAllocationSheet] = useState(false);
  const [allocationForm, setAllocationForm] = useState({
    equipment_id: '',
    operator_id: '',
    start_date: '',
    end_date: ''
  });
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobRes, customersRes, sitesRes, eqRes, opRes, typesRes, allocRes] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get('/customers'),
        api.get('/sites'),
        api.get('/equipment'),
        api.get('/operators'),
        api.get('/equipment-types'),
        api.get('/allocations')
      ]);
      
      setJob(jobRes.data);
      setCustomers(customersRes.data || []);
      setSites(sitesRes.data || []);
      setEquipment(eqRes.data || []);
      setOperators(opRes.data || []);
      setEquipmentTypes(typesRes.data || []);
      setAllocations(allocRes.data || []);
      
      // Initialize edit form
      setEditForm({
        customer_id: jobRes.data.customer_id || '',
        site_id: jobRes.data.site_id || '',
        equipment_type: jobRes.data.equipment_type || '',
        start_date: jobRes.data.start_date || '',
        end_date: jobRes.data.end_date || '',
        start_time: jobRes.data.start_time || '',
        description: jobRes.data.description || '',
        notes: jobRes.data.notes || ''
      });
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown';
  };

  const getSiteName = (siteId) => {
    if (!siteId) return '';
    const site = sites.find(s => s.id === siteId);
    return site?.name || '';
  };

  const getSiteAddress = (siteId) => {
    if (!siteId) return '';
    const site = sites.find(s => s.id === siteId);
    if (!site) return '';
    const parts = [site.street_address, site.suburb, site.state, site.postcode].filter(Boolean);
    return parts.join(', ') || site.address || '';
  };

  const getCustomerSites = (customerId) => {
    return sites.filter(s => s.customer_id === customerId);
  };

  const getEquipmentName = (eqId) => {
    const eq = equipment.find(e => e.id === eqId);
    return eq ? eq.asset_id : '';
  };

  const getOperatorName = (opId) => {
    const op = operators.find(o => o.id === opId);
    return op?.name || '';
  };

  const getJobAllocation = () => {
    return allocations.find(a => a.job_id === jobId);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/jobs/${jobId}`, {
        ...job,
        ...editForm,
        end_date: editForm.end_date || null
      });
      toast.success('Job updated');
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAllocation = () => {
    const existingAlloc = getJobAllocation();
    if (existingAlloc) {
      setAllocationForm({
        equipment_id: existingAlloc.equipment_id || '',
        operator_id: existingAlloc.operator_id || '',
        start_date: existingAlloc.start_date || job.start_date,
        end_date: existingAlloc.end_date || ''
      });
    } else {
      setAllocationForm({
        equipment_id: '',
        operator_id: '',
        start_date: job.start_date,
        end_date: job.end_date || ''
      });
    }
    setShowAllocationSheet(true);
  };

  const handleSaveAllocation = async () => {
    setSaving(true);
    try {
      const existingAlloc = getJobAllocation();
      const allocData = {
        job_id: jobId,
        ...allocationForm,
        end_date: allocationForm.end_date || allocationForm.start_date
      };

      if (existingAlloc) {
        await api.put(`/allocations/${existingAlloc.id}`, allocData);
        toast.success('Allocation updated');
      } else {
        await api.post('/allocations', allocData);
        // Update job status to allocated
        await api.put(`/jobs/${jobId}`, {
          ...job,
          status: 'allocated'
        });
        toast.success('Resources allocated');
      }
      setShowAllocationSheet(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allocation');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmJob = async () => {
    setSaving(true);
    try {
      await api.put(`/jobs/${jobId}`, {
        ...job,
        status: 'confirmed'
      });
      toast.success('Job confirmed - Now visible to operator');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm job');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateJob = async () => {
    try {
      const duplicateData = {
        customer_id: job.customer_id,
        site_id: job.site_id || '',
        equipment_type: job.equipment_type,
        start_date: job.start_date,
        start_time: job.start_time || null,
        end_date: job.end_date || null,
        description: job.description,
        status: 'not_allocated',
        notes: job.notes || ''
      };
      await api.post('/jobs', duplicateData);
      toast.success('Job duplicated');
      navigate('/tenant-mobile/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate job');
    }
  };

  const handleDeleteJob = async () => {
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success('Job deleted');
      navigate('/tenant-mobile/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
    setShowDeleteDialog(false);
  };

  const handleOpenJobChat = async () => {
    if (!job || !canUseChat) return;
    
    try {
      const room = await openJobChat(jobId);
      if (room) {
        navigate(`/tenant-mobile/chat/${room.id}`);
      } else {
        toast.error('Failed to open chat room');
      }
    } catch (error) {
      console.error('Failed to open job chat:', error);
      toast.error('Failed to open chat');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    return timeStr.slice(0, 5);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'EEE, d MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  // Get available equipment filtered by type
  const getAvailableEquipment = () => {
    if (!job?.equipment_type) return equipment.filter(e => e.status === 'available');
    return equipment.filter(e => 
      e.status === 'available' && e.equipment_type === job.equipment_type
    );
  };

  const availableEquipment = getAvailableEquipment();
  const availableOperators = operators.filter(o => o.is_available);

  if (loading) {
    return (
      <TenantMobileLayout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </TenantMobileLayout>
    );
  }

  if (!job) {
    return (
      <TenantMobileLayout>
        <div className="p-4 text-center">
          <p className="text-gray-400">Job not found</p>
          <Button 
            onClick={() => navigate('/tenant-mobile/jobs')}
            className="mt-4 btn-volt"
          >
            Back to Jobs
          </Button>
        </div>
      </TenantMobileLayout>
    );
  }

  const allocation = getJobAllocation();
  const isMultiDay = job.end_date && job.end_date !== job.start_date;

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tenant-mobile/jobs')} 
              className="text-gray-400 p-2"
              data-testid="back-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-white font-bold text-lg">Job Details</h1>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsEditing(true)}
                className="text-[#ffd108] p-2"
                data-testid="edit-btn"
              >
                <Pencil className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Status & Time Card */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#ffd108]" />
                <span className="text-2xl font-bold text-[#ffd108]">
                  {formatTime(job.start_time)}
                </span>
              </div>
              <StatusBadge status={job.status} />
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(job.start_date)}</span>
              {isMultiDay && (
                <>
                  <span>→</span>
                  <span>{formatDate(job.end_date)}</span>
                </>
              )}
            </div>
            {isMultiDay && (
              <Badge className="bg-purple-900 text-purple-300 text-xs mt-2">
                Multi-day Job
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Edit Mode */}
        {isEditing ? (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Pencil className="h-5 w-5 text-[#ffd108]" />
                Edit Job
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Customer</Label>
                <Select 
                  value={editForm.customer_id}
                  onValueChange={(v) => setEditForm({ ...editForm, customer_id: v, site_id: '' })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-700">
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Site */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Site</Label>
                <Select 
                  value={editForm.site_id}
                  onValueChange={(v) => setEditForm({ ...editForm, site_id: v })}
                  disabled={!editForm.customer_id}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-700">
                    {getCustomerSites(editForm.customer_id).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Equipment Type */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Asset Type</Label>
                <Select 
                  value={editForm.equipment_type}
                  onValueChange={(v) => setEditForm({ ...editForm, equipment_type: v })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-700">
                    {equipmentTypes.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs uppercase">Start Date</Label>
                  <Input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs uppercase">End Date</Label>
                  <Input
                    type="date"
                    value={editForm.end_date || ''}
                    onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                    min={editForm.start_date}
                  />
                </div>
              </div>

              {/* Time */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Start Time</Label>
                <Input
                  type="time"
                  value={editForm.start_time || ''}
                  onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white"
                  rows={3}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Notes</Label>
                <Textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white"
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-gray-700"
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 btn-volt"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  data-testid="save-job-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Customer & Location */}
            <Card className="bg-[#2a2a2a] border-gray-700">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-[#ffd108] mt-0.5" />
                  <div>
                    <p className="text-white font-medium">{getCustomerName(job.customer_id)}</p>
                    <p className="text-gray-400 text-sm">Customer</p>
                  </div>
                </div>
                
                {job.site_id && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#ffd108] mt-0.5" />
                    <div>
                      <p className="text-white font-medium">{getSiteName(job.site_id)}</p>
                      <p className="text-gray-400 text-sm">{getSiteAddress(job.site_id)}</p>
                    </div>
                  </div>
                )}

                {job.equipment_type && (
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 text-[#ffd108] mt-0.5" />
                    <div>
                      <p className="text-white font-medium">{job.equipment_type}</p>
                      <p className="text-gray-400 text-sm">Asset Required</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description */}
            {job.description && (
              <Card className="bg-[#2a2a2a] border-gray-700">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-xs uppercase mb-2">Description</p>
                  <p className="text-white">{job.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {job.notes && (
              <Card className="bg-[#2a2a2a] border-gray-700">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-xs uppercase mb-2">Notes</p>
                  <p className="text-gray-300 text-sm">{job.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Allocation Info */}
            <Card className="bg-[#2a2a2a] border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#ffd108]" />
                  Resource Allocation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {allocation ? (
                  <>
                    {allocation.equipment_id && (
                      <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] rounded-lg">
                        <Truck className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="text-white font-medium">{getEquipmentName(allocation.equipment_id)}</p>
                          <p className="text-gray-400 text-xs">Asset</p>
                        </div>
                      </div>
                    )}
                    {allocation.operator_id && (
                      <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] rounded-lg">
                        <User className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium">{getOperatorName(allocation.operator_id)}</p>
                          <p className="text-gray-400 text-xs">Operator</p>
                        </div>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-700 text-white"
                      onClick={handleOpenAllocation}
                      data-testid="edit-allocation-btn"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Allocation
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <AlertTriangle className="h-10 w-10 mx-auto text-amber-400 mb-2" />
                    <p className="text-gray-400 text-sm mb-3">No resources allocated yet</p>
                    <Button
                      className="btn-volt"
                      onClick={handleOpenAllocation}
                      data-testid="allocate-btn"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Allocate Resources
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Chat Button - show if messaging feature available */}
              {canUseChat && (
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleOpenJobChat}
                  data-testid="job-chat-btn"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Open Job Chat
                </Button>
              )}

              {/* Confirm Button - show if allocated but not confirmed */}
              {job.status === 'allocated' && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleConfirmJob}
                  disabled={saving}
                  data-testid="confirm-job-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Confirm Job
                </Button>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="border-gray-700 text-white"
                  onClick={handleDuplicateJob}
                  data-testid="duplicate-btn"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  className="border-red-800 text-red-400 hover:bg-red-900/20"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Allocation Sheet */}
      <Sheet open={showAllocationSheet} onOpenChange={setShowAllocationSheet}>
        <SheetContent side="bottom" className="bg-[#2a2a2a] border-gray-700 h-[80vh]">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-[#ffd108]" />
              Allocate Resources
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto">
            {/* Equipment */}
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Asset</Label>
              <Select 
                value={allocationForm.equipment_id}
                onValueChange={(v) => setAllocationForm({ ...allocationForm, equipment_id: v })}
              >
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] border-gray-700">
                  {availableEquipment.map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.asset_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableEquipment.length === 0 && (
                <p className="text-amber-400 text-xs">No available equipment of this type</p>
              )}
            </div>

            {/* Operator */}
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs uppercase">Operator</Label>
              <Select 
                value={allocationForm.operator_id}
                onValueChange={(v) => setAllocationForm({ ...allocationForm, operator_id: v })}
              >
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] border-gray-700">
                  {availableOperators.map(op => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">Start Date</Label>
                <Input
                  type="date"
                  value={allocationForm.start_date}
                  onChange={(e) => setAllocationForm({ ...allocationForm, start_date: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs uppercase">End Date</Label>
                <Input
                  type="date"
                  value={allocationForm.end_date || ''}
                  onChange={(e) => setAllocationForm({ ...allocationForm, end_date: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700 text-white font-mono"
                  min={allocationForm.start_date}
                />
              </div>
            </div>
          </div>

          <SheetFooter className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 border-gray-700"
              onClick={() => setShowAllocationSheet(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 btn-volt"
              onClick={handleSaveAllocation}
              disabled={saving || (!allocationForm.equipment_id && !allocationForm.operator_id)}
              data-testid="save-allocation-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save Allocation
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#2a2a2a] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Job?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete this job and all associated allocations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteJob}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TenantMobileLayout>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  // Status colors use CSS variables that change based on theme
  const baseClass = "border";
  switch (status) {
    case 'not_allocated':
      return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-not-allocated-bg)', color: 'var(--status-not-allocated-text)', borderColor: 'var(--status-not-allocated-border)' }}>Not Allocated</Badge>;
    case 'allocated':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-allocated-bg)', color: 'var(--status-allocated-text)', borderColor: 'var(--status-allocated-border)' }}>Allocated</Badge>;
    case 'confirmed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-confirmed-bg)', color: 'var(--status-confirmed-text)', borderColor: 'var(--status-confirmed-border)' }}>Confirmed</Badge>;
    case 'docketed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-docketed-bg)', color: 'var(--status-docketed-text)', borderColor: 'var(--status-docketed-border)' }}>Docketed</Badge>;
    case 'invoiced':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-invoiced-bg)', color: 'var(--status-invoiced-text)', borderColor: 'var(--status-invoiced-border)' }}>Invoiced</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export default TenantMobileJobDetail;
